<script lang="ts">
	import type { Zoi } from '$lib/components/zoi/types';
	import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
	import type { ClassValue } from 'svelte/elements';
	import RouteErrorImage from '$assets/route/route-error.svg';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import FilterButton from '$lib/components/pois/list/FilterButton.svelte';
	import ZoiCard from '$lib/components/zoi/ZoiCard/ZoiCard.svelte';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { createZoiUrl } from '$lib/utils/createZoiUrl';
	import * as m from '$paraglide/messages';
	import emblaCarouselSvelte from 'embla-carousel-svelte';

	interface Props {
		hasErrors: boolean;
		isLoading: boolean;
		zois: Zoi[];
		class: ClassValue;
		onSelectZoi: (zoi: Zoi, zoiPosition: number) => void;
		onOpenSettings: () => void;
		onRetry: () => void;
	}

	const {
		hasErrors,
		isLoading,
		zois,
		class: classes = '',
		onSelectZoi,
		onOpenSettings,
		onRetry,
	}: Props = $props();

	const controlsClasses = 'text-3.5 size-7.5 px-0 z-100 absolute top-50% translate-y-[-50%] shadow-recommendation-card rd-50% opacity-90 hidden md-block';
	const searchParams = getSearchParamsStore();
	const options: EmblaOptionsType = {
		loop: false,
	};
	let emblaApi: EmblaCarouselType;
	let currentIndex = $state(0);

	function onInit(event: CustomEvent<EmblaCarouselType>) {
		emblaApi = event.detail;
	}
</script>

{#if hasErrors}
	<div class="flex flex-col gap-4 items-center justify-center w-full">
		<div class="flex flex-col gap-2 justify-center item-center">
			<img class="w-22 h-22 w-full m-b-2" src={RouteErrorImage} alt="Error" />
			<div class="text-center">{m.zoi_card__error()}</div>
		</div>
		<Button
			icon="autorenew"
			text={m.action__retry()}
			size="sm"
			onclick={onRetry}
		/>
	</div>
{:else}
	<div class={classes} data-testid="zoi-list">
		<div class="flex justify-between items-center">
			<div class="text-md text-coloredneutral font-600">{m.zoi__title()}</div>
			<div class="ml-2 relative order-1 py-0.5">
				<FilterButton classes="px-0" color="primary" variant="ghost" onclick={onOpenSettings} />
			</div>
		</div>
		<div
			class={['embla overflow-hidden relative mx--2 md:m-l--1.5 md:m-r--2']}
			use:emblaCarouselSvelte={{ options, plugins: [] }}
			onemblaInit={onInit}>
			<ul class="embla__container list-reset flex gap-2 py-1!">
				{#if isLoading}
					{#each { length: 2 } as _, index (index)}
						<ZoiCard isLoading class="embla__slide" />
					{/each}
				{:else if zois.length > 0}
					{#each zois as zoi, index (zoi.zoiId)}
						<ZoiCard
							images={zoi.images ?? undefined}
							title={zoi.name}
							href={createZoiUrl($searchParams, zoi)}
							dataTestId={`zoi-card-${index}`}
							onclick={() => onSelectZoi(zoi, index + 1)}
							class={['embla__slide', index === 0 && 'ml-2!', index === zois.length - 1 && 'mr-2!']}
						/>
					{/each}
				{/if}
			</ul>
			{#if zois.length > 1 && currentIndex > 0}
				<Button
					color="white"
					icon="west"
					variant="filled"
					ariaLabel={m.poi_card__carousel__next_button()}
					iconClasses="m-auto"
					class={['embla__prev left-2', controlsClasses]}
					onclick={() => {
						if (currentIndex > 0 && emblaApi.canScrollPrev()) {
							emblaApi.scrollPrev();
							currentIndex--;
						}
					}}
				/>
			{/if}
			{#if zois.length > 1 && currentIndex < zois.length - 1}
				<Button
					color="white"
					icon="east"
					variant="filled"
					ariaLabel={m.poi_card__carousel__prev_button()}
					iconClasses="m-auto"
					class={['embla__next right-2', controlsClasses]}
					onclick={() => {
						if (currentIndex < zois.length - 1 && emblaApi.canScrollNext()) {
							emblaApi.scrollNext();
							currentIndex++;
						}
					}}
				/>
			{/if}
		</div>
	</div>
{/if}
